<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import ContactsAPI from 'dashboard/api/contacts';

export default {
  name: 'InteractiveMessageBuilderModal',
  components: {
    WootMessageEditor,
    EmojiInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      required: true,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['send', 'close', 'update:show'],
  setup() {
    return {
      useAlert,
    };
  },
  data() {
    return {
      messageContent: '',
      messageType: '', // Will be set based on channel type in resetForm
      options: [''], // Array of option values
      localShow: false,
      showEmojiPicker: false,
      // Header/Footer
      header: {
        enabled: false,
        type: 'text',
        text: '',
        link: '',
      },
      footer: '',
      // Buttons (WhatsApp quick reply buttons - max 3)
      buttons: ['', '', ''], // Up to 3 quick reply buttons
      // List
      buttonText: '', // Button text for list
      listSections: [
        {
          title: 'Section 1',
          rows: [{ title: '', description: '' }],
        },
      ],
      // Cards
      cards: [],
      // Form
      formItems: [
        { label: '', placeholder: '', name: '', type: 'text', required: false },
      ],
      formButtonLabel: 'Submit',
      // Articles
      articles: [{ title: '', description: '', link: '' }],
      // Product Catalog (handles both single and multi-product)
      catalogId: '',
      selectedProducts: [''], // Array of product retailer IDs
      // Flow
      flowId: '',
      flowCta: '',
      flowAction: 'navigate',
      flowToken: '',
      // Location
      latitude: '',
      longitude: '',
      locationName: '',
      locationAddress: '',
      // URL Button (single CTA button)
      urlButtonTitle: '',
      urlButtonUrl: '',
      // WAHA Poll
      pollName: '',
      pollOptions: ['', ''],
      pollMultiSelect: false,
      // WAHA Contact vCard
      selectedContactIds: [],
      availableContacts: [],
      loadingContacts: false,
      contactSearchQuery: '',
      contactSearchTimeout: null,
      // WhatsApp API data
      catalogs: [],
      products: [],
      flows: [],
      loadingCatalogs: false,
      loadingProducts: false,
      loadingFlows: false,
      // Telegram Inline Keyboard (button grid)
      telegramButtonRows: [[{ text: '', type: 'callback', url: '' }]],
      // Telegram Poll
      telegramPollQuestion: '',
      telegramPollOptions: ['', ''],
      telegramPollIsAnonymous: true,
      telegramPollAllowsMultiple: false,
      telegramPollType: 'regular',
      telegramPollCorrectOption: 0,
      // Facebook Quick Replies (max 13 buttons, text callbacks only)
      fbQuickReplies: [{ text: '' }],
      // Facebook Buttons (URL, Coupon, Postback)
      fbButtons: [{ text: '', type: 'url', value: '' }],
      // Facebook Customer Information Template
      customerInfoPrivacyUrl: '',
      customerInfoCountries: [],
      // Facebook Coupon Template
      couponCode: '',
      couponUrl: '',
    };
  },
  computed: {
    isWebWidgetOrAPI() {
      const channelType = this.inbox?.channel_type || '';
      return (
        channelType === 'Channel::WebWidget' || channelType === 'Channel::Api'
      );
    },
    isWhatsAppChannel() {
      const channelType = this.inbox?.channel_type || '';
      return channelType?.toLowerCase().includes('whatsapp');
    },
    isWahaChannel() {
      const channelType = this.inbox?.channel_type || '';
      return channelType === 'Channel::Api' && this.inbox?.waha_session;
    },
    isTelegramChannel() {
      const channelType = this.inbox?.channel_type || '';
      return channelType === 'Channel::Telegram';
    },
    isFacebookChannel() {
      const channelType = this.inbox?.channel_type || '';
      return channelType === 'Channel::FacebookPage';
    },
    isInstagramChannel() {
      const channelType = this.inbox?.channel_type || '';
      return channelType === 'Channel::Instagram';
    },
    validationError() {
      if (!this.messageContent.trim()) {
        return this.$t(
          'CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.ERRORS.MESSAGE_REQUIRED'
        );
      }

      switch (this.messageType) {
        case 'options': {
          const validOptions = this.options.filter(opt => opt && opt.trim());
          if (validOptions.length === 0) {
            return 'At least one option is required';
          }
          break;
        }

        case 'catalog_product': {
          if (!this.catalogId.trim()) {
            return 'Please select a catalog';
          }
          const validProducts = this.selectedProducts.filter(
            p => p && p.trim()
          );
          if (validProducts.length === 0) {
            return 'Please select at least one product';
          }
          break;
        }

        case 'flow': {
          if (!this.flowId.trim()) {
            return 'Flow ID is required';
          }
          if (!this.flowCta.trim()) {
            return 'Flow button text (CTA) is required';
          }
          break;
        }

        case 'location': {
          if (!this.latitude || !this.longitude) {
            return 'Latitude and Longitude are required';
          }
          const lat = parseFloat(this.latitude);
          const lng = parseFloat(this.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return 'Please enter valid latitude and longitude';
          }
          if (lat < -90 || lat > 90) {
            return 'Latitude must be between -90 and 90';
          }
          if (lng < -180 || lng > 180) {
            return 'Longitude must be between -180 and 180';
          }
          break;
        }

        case 'cards': {
          const validCards = this.cards.filter(
            card => card.title && card.title.trim()
          );
          if (validCards.length === 0) {
            return 'At least one card with a title is required';
          }
          break;
        }

        case 'form': {
          const validFields = this.formItems.filter(
            item => item.label && item.label.trim()
          );
          if (validFields.length === 0) {
            return 'At least one form field with a label is required';
          }
          break;
        }

        case 'article': {
          const validArticles = this.articles.filter(
            article => article.title && article.link
          );
          if (validArticles.length === 0) {
            return 'At least one article with title and link is required';
          }
          // Validate URLs
          for (const article of validArticles) {
            if (
              !article.link.startsWith('http://') &&
              !article.link.startsWith('https://')
            ) {
              return 'Article links must be valid URLs (http:// or https://)';
            }
          }
          break;
        }

        case 'buttons': {
          // WhatsApp quick reply buttons (max 3)
          const validButtons = this.buttons.filter(btn => btn && btn.trim());
          if (validButtons.length === 0) {
            return 'At least one button is required';
          }
          if (validButtons.length > 3) {
            return 'Maximum 3 buttons allowed for WhatsApp';
          }
          break;
        }

        case 'list': {
          // WhatsApp list requires button text and at least one section with rows
          if (!this.buttonText.trim()) {
            return 'Button text is required for list';
          }
          const validSections = this.listSections.filter(section => {
            const validRows = section.rows.filter(
              row => row.title && row.title.trim()
            );
            return validRows.length > 0;
          });
          if (validSections.length === 0) {
            return 'At least one section with valid rows is required';
          }
          break;
        }

        case 'waha_poll': {
          // WAHA Poll validation
          if (!this.pollName.trim()) {
            return 'Poll question is required';
          }
          const validPollOptions = this.pollOptions.filter(
            opt => opt && opt.trim()
          );
          if (validPollOptions.length < 2) {
            return 'At least 2 poll options are required';
          }
          break;
        }

        case 'waha_contact': {
          // WAHA Contact vCard validation
          if (
            !this.selectedContactIds ||
            this.selectedContactIds.length === 0
          ) {
            return 'Please select at least one contact to share';
          }
          break;
        }

        case 'waha_list': {
          // WAHA List validation (same as WhatsApp list)
          if (!this.buttonText.trim()) {
            return 'Button text is required for list';
          }
          const validWahaListSections = this.listSections.filter(section => {
            const validRows = section.rows.filter(
              row => row.title && row.title.trim()
            );
            return validRows.length > 0;
          });
          if (validWahaListSections.length === 0) {
            return 'At least one section with valid rows is required';
          }
          break;
        }

        case 'telegram_buttons': {
          // Telegram inline keyboard validation
          let hasValidButton = false;
          for (const row of this.telegramButtonRows) {
            for (const button of row) {
              if (button.text && button.text.trim()) {
                hasValidButton = true;
                // Validate URL buttons have valid URLs
                if (button.type === 'url') {
                  const url = button.url?.trim() || '';
                  if (!url) {
                    return 'URL buttons must have a URL';
                  }
                  if (
                    !url.startsWith('http://') &&
                    !url.startsWith('https://')
                  ) {
                    return 'URLs must start with http:// or https://';
                  }
                }
              }
            }
          }
          if (!hasValidButton) {
            return 'At least one button is required';
          }
          break;
        }

        case 'fb_quick_replies': {
          // Facebook quick replies validation (text callbacks only)
          const validReplies = this.fbQuickReplies.filter(
            reply => reply.text && reply.text.trim()
          );
          if (validReplies.length === 0) {
            return 'At least one quick reply is required';
          }
          if (validReplies.length > 13) {
            return 'Maximum 13 quick replies allowed for Facebook';
          }
          break;
        }

        case 'telegram_poll': {
          // Telegram poll validation
          if (!this.telegramPollQuestion.trim()) {
            return 'Poll question is required';
          }
          const validPollOptions = this.telegramPollOptions.filter(
            opt => opt && opt.trim()
          );
          if (validPollOptions.length < 2) {
            return 'At least 2 poll options are required';
          }
          if (validPollOptions.length > 10) {
            return 'Maximum 10 poll options allowed';
          }
          break;
        }

        case 'fb_buttons': {
          // Facebook buttons validation
          const validButtons = this.fbButtons.filter(
            btn => btn.text && btn.text.trim()
          );
          if (validButtons.length === 0) {
            return 'At least one button is required';
          }
          // Validate each button has required value based on type
          for (const button of validButtons) {
            if (button.type === 'url') {
              if (!button.value || !button.value.trim()) {
                return 'URL is required for URL buttons';
              }
              // Validate URL format
              if (
                !button.value.startsWith('http://') &&
                !button.value.startsWith('https://')
              ) {
                return 'URL must start with http:// or https://';
              }
            }
          }
          break;
        }

        case 'coupon': {
          // Facebook coupon template validation
          if (!this.couponCode.trim()) {
            return 'Coupon code is required';
          }
          // Validate URL format if URL is provided
          if (this.couponUrl.trim()) {
            if (
              !this.couponUrl.startsWith('http://') &&
              !this.couponUrl.startsWith('https://')
            ) {
              return 'Coupon URL must start with http:// or https://';
            }
          }
          break;
        }

        case 'customer_information': {
          // Facebook customer information template validation
          if (!this.customerInfoPrivacyUrl.trim()) {
            return 'Privacy policy URL is required';
          }
          // Validate URL format
          if (
            !this.customerInfoPrivacyUrl.startsWith('http://') &&
            !this.customerInfoPrivacyUrl.startsWith('https://')
          ) {
            return 'Privacy policy URL must start with http:// or https://';
          }
          break;
        }

        default:
          break;
      }

      return null;
    },
    isValid() {
      return !this.validationError;
    },
  },
  watch: {
    show(value) {
      console.log(
        'InteractiveMessageBuilderModal: show prop changed to:',
        value
      );
      this.localShow = value;
      if (value) {
        this.resetForm();
      }
    },
    localShow(value) {
      this.$emit('update:show', value);
    },
    messageType(newType) {
      if (newType === 'catalog_product') {
        this.fetchCatalogs();
      } else if (newType === 'flow') {
        this.fetchFlows();
      } else if (newType === 'waha_contact') {
        this.fetchContacts();
      }
    },
    catalogId(newCatalogId) {
      if (newCatalogId && this.messageType === 'catalog_product') {
        this.fetchProducts(newCatalogId);
      }
    },
  },
  methods: {
    generateValueFromTitle(title) {
      // Convert title to a valid value: lowercase, replace spaces with underscores, remove special chars
      return title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 20); // Max 20 chars for value
    },
    addOption() {
      this.options.push('');
    },
    removeOption(index) {
      if (this.options.length > 1) {
        this.options.splice(index, 1);
      }
    },
    addProductToCatalog() {
      this.selectedProducts.push('');
    },
    removeProductFromCatalog(index) {
      if (this.selectedProducts.length > 1) {
        this.selectedProducts.splice(index, 1);
      }
    },
    addCard() {
      this.cards.push({
        title: '',
        description: '',
        media_url: '',
        actions: [{ text: '', type: 'link', uri: '' }],
      });
    },
    removeCard(index) {
      if (this.cards.length > 1) {
        this.cards.splice(index, 1);
      }
    },
    addCardAction(cardIndex) {
      this.cards[cardIndex].actions.push({ text: '', type: 'link', uri: '' });
    },
    removeCardAction(cardIndex, actionIndex) {
      if (this.cards[cardIndex].actions.length > 1) {
        this.cards[cardIndex].actions.splice(actionIndex, 1);
      }
    },
    addFormField() {
      this.formItems.push({
        label: '',
        placeholder: '',
        name: '',
        type: 'text',
        required: false,
      });
    },
    removeFormField(index) {
      if (this.formItems.length > 1) {
        this.formItems.splice(index, 1);
      }
    },
    addArticle() {
      this.articles.push({ title: '', description: '', link: '' });
    },
    removeArticle(index) {
      if (this.articles.length > 1) {
        this.articles.splice(index, 1);
      }
    },
    addListSection() {
      this.listSections.push({
        title: `Section ${this.listSections.length + 1}`,
        rows: [{ title: '', description: '' }],
      });
    },
    removeListSection(index) {
      if (this.listSections.length > 1) {
        this.listSections.splice(index, 1);
      }
    },
    addListRow(sectionIndex) {
      this.listSections[sectionIndex].rows.push({ title: '', description: '' });
    },
    removeListRow(sectionIndex, rowIndex) {
      if (this.listSections[sectionIndex].rows.length > 1) {
        this.listSections[sectionIndex].rows.splice(rowIndex, 1);
      }
    },
    resetForm() {
      this.messageContent = '';

      // Set default message type based on channel
      if (this.isTelegramChannel) {
        this.messageType = 'telegram_buttons';
      } else if (this.isFacebookChannel) {
        this.messageType = 'fb_quick_replies';
      } else if (this.isInstagramChannel) {
        this.messageType = 'fb_quick_replies';
      } else if (this.isWhatsAppChannel) {
        this.messageType = 'buttons';
      } else if (this.isWahaChannel) {
        this.messageType = 'waha_poll';
      } else {
        this.messageType = 'options';
      }

      this.options = [''];
      this.cards = [];
      this.catalogId = '';
      this.selectedProducts = [''];
      this.flowId = '';
      this.flowCta = '';
      this.flowAction = 'navigate';
      this.flowToken = '';
      this.latitude = '';
      this.longitude = '';
      this.locationName = '';
      this.locationAddress = '';
      this.urlButtonTitle = '';
      this.urlButtonUrl = '';
      this.buttons = ['', '', ''];
      this.buttonText = '';
      this.listSections = [
        {
          title: 'Section 1',
          rows: [{ title: '', description: '' }],
        },
      ];
      this.header = {
        enabled: false,
        type: 'text',
        text: '',
        link: '',
      };
      this.footer = '';
      // WAHA Poll reset
      this.pollName = '';
      this.pollOptions = ['', ''];
      this.pollMultiSelect = false;
      // WAHA Contact reset
      this.selectedContactIds = [];
      this.availableContacts = [];
      this.contactSearchQuery = '';
      // Telegram reset
      this.telegramButtonRows = [[{ text: '', type: 'callback', url: '' }]];
      this.telegramPollQuestion = '';
      this.telegramPollOptions = ['', ''];
      this.telegramPollIsAnonymous = true;
      this.telegramPollAllowsMultiple = false;
      this.telegramPollType = 'regular';
      this.telegramPollCorrectOption = 0;
      // Facebook reset
      this.fbQuickReplies = [{ text: '', type: 'text' }];
      this.fbButtons = [{ text: '', type: 'url', value: '' }];
      this.couponCode = '';
      this.couponUrl = '';
      this.customerInfoPrivacyUrl = '';
      this.customerInfoCountries = [];
    },
    onClose() {
      this.localShow = false;
      this.$emit('close');
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    addEmojiToContent(emoji) {
      this.messageContent += emoji;
      this.hideEmojiPicker();
    },
    async sendInteractiveMessage() {
      if (!this.isValid) return;

      let messageData = {
        message: this.messageContent.trim(),
      };

      // Build payload based on message type
      switch (this.messageType) {
        case 'options': {
          messageData.content_type = 'input_select';
          const validOptions = this.options.filter(opt => opt && opt.trim());
          messageData.content_attributes = {
            items: validOptions.map(option => {
              const value = option.trim();
              return {
                title: value, // Display text shown to user
                value: this.generateValueFromTitle(value), // Internal ID
              };
            }),
          };
          break;
        }

        case 'catalog_product': {
          const validProducts = this.selectedProducts.filter(
            p => p && p.trim()
          );

          // Automatically determine if single or multi-product
          if (validProducts.length === 1) {
            // Single product message
            messageData.content_type = 'catalog_product';
            messageData.content_attributes = {
              catalog_id: this.catalogId.trim(),
              product_retailer_id: validProducts[0].trim(),
            };
          } else {
            // Multi-product catalog message
            messageData.content_type = 'catalog_product_list';
            messageData.content_attributes = {
              catalog_id: this.catalogId.trim(),
              sections: [
                {
                  title: 'Products',
                  product_items: validProducts.map(productId => ({
                    product_retailer_id: productId.trim(),
                  })),
                },
              ],
            };
            if (this.header.enabled && this.header.text) {
              messageData.content_attributes.header = this.header.text;
            }
          }

          if (this.footer.trim()) {
            messageData.content_attributes.footer = this.footer.trim();
          }
          break;
        }

        case 'flow': {
          messageData.content_type = 'flow';
          messageData.content_attributes = {
            flow_id: this.flowId.trim(),
            flow_cta: this.flowCta.trim(),
            flow_action: this.flowAction,
          };
          if (this.flowToken.trim()) {
            messageData.content_attributes.flow_token = this.flowToken.trim();
          }
          if (this.header.enabled && this.header.text) {
            messageData.content_attributes.header = this.header.text;
          }
          if (this.footer.trim()) {
            messageData.content_attributes.footer = this.footer.trim();
          }
          break;
        }

        case 'url_button': {
          // URL button (single CTA button)
          messageData.content_type = 'input_select';
          messageData.content_attributes = {
            items: [
              {
                title: this.urlButtonTitle.trim(),
                url: this.urlButtonUrl.trim(),
              },
            ],
          };

          // Add header if enabled
          if (this.header.enabled) {
            if (this.header.type === 'text' && this.header.text) {
              messageData.content_attributes.header = this.header.text;
            } else if (this.header.link) {
              messageData.content_attributes.header = {
                type: this.header.type,
                link: this.header.link,
              };
            }
          }

          // Add footer if provided
          if (this.footer.trim()) {
            messageData.content_attributes.footer = this.footer.trim();
          }
          break;
        }

        case 'cards': {
          messageData.content_type = 'cards';
          const validCards = this.cards.filter(
            card => card.title && card.title.trim()
          );
          messageData.content_attributes = {
            items: validCards.map(card => ({
              title: card.title.trim(),
              description: card.description?.trim() || '',
              media_url: card.media_url?.trim() || '',
              actions: card.actions
                .filter(action => action.text && action.text.trim())
                .map(action => {
                  const buttonAction = {
                    text: action.text.trim(),
                    type: action.type || 'link',
                  };
                  if (action.type === 'link') {
                    let uri = action.uri?.trim() || '';
                    // Ensure URL has protocol
                    if (
                      uri &&
                      !uri.startsWith('http://') &&
                      !uri.startsWith('https://')
                    ) {
                      uri = 'https://' + uri;
                    }
                    buttonAction.uri = uri;
                  } else {
                    // Auto-fill payload from button text if empty
                    buttonAction.payload =
                      action.payload?.trim() || action.text.trim();
                  }
                  return buttonAction;
                }),
            })),
          };
          break;
        }

        case 'form': {
          messageData.content_type = 'form';
          const validFields = this.formItems.filter(
            item => item.label && item.label.trim()
          );
          messageData.content_attributes = {
            items: validFields.map(item => ({
              label: item.label.trim(),
              name:
                item.name.trim() ||
                item.label.trim().toLowerCase().replace(/\s+/g, '_'),
              placeholder: item.placeholder?.trim() || '',
              type: item.type || 'text',
              required: item.required || false,
            })),
            button_label: this.formButtonLabel.trim() || 'Submit',
          };
          break;
        }

        case 'article': {
          messageData.content_type = 'article';
          const validArticles = this.articles.filter(
            article => article.title && article.link
          );
          messageData.content_attributes = {
            items: validArticles.map(article => ({
              title: article.title.trim(),
              description: article.description?.trim() || '',
              link: article.link.trim(),
            })),
          };
          break;
        }

        case 'location': {
          // Location is sent as text message with attachment
          messageData.content_type = 'text';
          messageData.content_attributes = {
            location: {
              latitude: parseFloat(this.latitude),
              longitude: parseFloat(this.longitude),
              name: this.locationName.trim(),
              address: this.locationAddress.trim(),
            },
          };
          break;
        }

        case 'buttons': {
          // WhatsApp quick reply buttons (max 3)
          messageData.content_type = 'input_select';
          const validButtons = this.buttons.filter(btn => btn && btn.trim());
          messageData.content_attributes = {
            items: validButtons.map(btn => ({
              title: btn.trim(),
              value: btn.trim(),
            })),
          };

          // Add header if enabled
          if (this.header.enabled) {
            if (this.header.type === 'text' && this.header.text) {
              messageData.content_attributes.header = this.header.text;
            } else if (this.header.link) {
              messageData.content_attributes.header = {
                type: this.header.type,
                link: this.header.link,
              };
            }
          }

          // Add footer if provided
          if (this.footer.trim()) {
            messageData.content_attributes.footer = this.footer.trim();
          }
          break;
        }

        case 'list': {
          // WhatsApp list menu
          messageData.content_type = 'input_select';
          messageData.content_attributes = {
            button_text: this.buttonText.trim(),
            sections: this.listSections
              .filter(section => {
                const validRows = section.rows.filter(
                  row => row.title && row.title.trim()
                );
                return validRows.length > 0;
              })
              .map(section => ({
                title: section.title.trim() || 'Section',
                rows: section.rows
                  .filter(row => row.title && row.title.trim())
                  .map(row => ({
                    title: row.title.trim(),
                    description: row.description?.trim() || '',
                  })),
              })),
          };

          // Add header if enabled (text only for lists)
          if (this.header.enabled && this.header.text) {
            messageData.content_attributes.header = this.header.text;
          }

          // Add footer if provided
          if (this.footer.trim()) {
            messageData.content_attributes.footer = this.footer.trim();
          }
          break;
        }

        case 'waha_poll': {
          // WAHA Poll message
          messageData.content_type = 'waha_poll';
          const validPollOptions = this.pollOptions.filter(
            opt => opt && opt.trim()
          );
          messageData.content_attributes = {
            name: this.pollName.trim(),
            options: validPollOptions.map(opt => opt.trim()),
            selectableOptionsCount: this.pollMultiSelect
              ? validPollOptions.length
              : 1,
          };
          break;
        }

        case 'waha_contact': {
          // WAHA Contact vCard message
          messageData.content_type = 'waha_contact';
          messageData.content_attributes = {
            contact_ids: this.selectedContactIds,
          };
          break;
        }

        case 'waha_list': {
          // WAHA List message
          messageData.content_type = 'waha_list';
          messageData.content_attributes = {
            title: this.messageContent.trim(),
            button: this.buttonText.trim(),
            sections: this.listSections
              .filter(section => {
                const validRows = section.rows.filter(
                  row => row.title && row.title.trim()
                );
                return validRows.length > 0;
              })
              .map((section, sectionIndex) => ({
                title: section.title.trim() || 'Section',
                rows: section.rows
                  .filter(row => row.title && row.title.trim())
                  .map((row, rowIndex) => ({
                    rowId: `row_${sectionIndex}_${rowIndex}`,
                    title: row.title.trim(),
                    description: row.description?.trim() || '',
                  })),
              })),
          };
          break;
        }

        case 'telegram_buttons': {
          // Telegram inline keyboard
          messageData.content_type = 'input_select';
          const items = [];
          this.telegramButtonRows.forEach((row, rowIndex) => {
            row.forEach((button, btnIndex) => {
              if (button.text && button.text.trim()) {
                const item = {
                  title: button.text.trim(),
                  row: rowIndex,
                  col: btnIndex,
                };

                // Add url or value based on button type
                if (button.type === 'url' && button.url && button.url.trim()) {
                  item.url = button.url.trim();
                } else {
                  item.value = this.generateValueFromTitle(button.text.trim());
                }

                items.push(item);
              }
            });
          });
          messageData.content_attributes = { items };
          break;
        }

        case 'fb_quick_replies': {
          // Facebook quick replies (text callbacks only)
          messageData.content_type = 'input_select';
          const validReplies = this.fbQuickReplies.filter(
            reply => reply.text && reply.text.trim()
          );
          messageData.content_attributes = {
            items: validReplies.map(reply => ({
              title: reply.text.trim(),
              value: this.generateValueFromTitle(reply.text.trim()),
            })),
          };
          break;
        }

        case 'telegram_poll': {
          // Telegram poll (always regular, always non-anonymous)
          messageData.content_type = 'poll';
          const validPollOptions = this.telegramPollOptions.filter(
            opt => opt && opt.trim()
          );
          messageData.content_attributes = {
            question: this.telegramPollQuestion.trim(),
            options: validPollOptions.map(opt => opt.trim()),
            is_anonymous: false, // Always non-anonymous so we get poll_answer webhooks
            allows_multiple_answers: this.telegramPollAllowsMultiple,
            type: 'regular',
          };
          break;
        }

        case 'fb_buttons': {
          // Facebook Button Template (URL and Postback buttons)
          const validButtons = this.fbButtons.filter(
            btn => btn.text && btn.text.trim()
          );

          messageData.content_type = 'fb_buttons';
          messageData.content_attributes = {
            buttons: validButtons.map(btn => {
              if (btn.type === 'url') {
                return {
                  type: 'web_url',
                  title: btn.text.trim(),
                  url: btn.value.trim(),
                };
              }
              // postback type
              return {
                type: 'postback',
                title: btn.text.trim(),
                payload: btn.value.trim() || btn.text.trim(),
              };
            }),
          };
          break;
        }

        case 'coupon': {
          // Facebook Coupon Template
          messageData.content_type = 'coupon';
          messageData.content_attributes = {
            coupon_code: this.couponCode.trim(),
          };

          // Add optional URL if provided
          if (this.couponUrl.trim()) {
            messageData.content_attributes.coupon_url = this.couponUrl.trim();
            messageData.content_attributes.coupon_url_button_title =
              'View Offer';
          }
          break;
        }

        case 'customer_information': {
          // Facebook customer information template
          messageData.content_type = 'customer_information';
          messageData.content_attributes = {
            business_privacy_url: this.customerInfoPrivacyUrl.trim(),
          };

          // Add optional countries filter
          if (
            this.customerInfoCountries &&
            this.customerInfoCountries.length > 0
          ) {
            messageData.content_attributes.countries =
              this.customerInfoCountries;
          }
          break;
        }
      }

      try {
        this.$emit('send', messageData);
        this.onClose();

        useAlert(
          this.$t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          this.$t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.ERROR_MESSAGE')
        );
      }
    },
    async fetchCatalogs() {
      if (!this.inboxId) return;

      this.loadingCatalogs = true;
      try {
        const response = await InboxesAPI.getWhatsAppCatalogs(this.inboxId);
        this.catalogs = response.data || [];
      } catch (error) {
        console.error('Failed to fetch catalogs:', error);
        this.catalogs = [];
      } finally {
        this.loadingCatalogs = false;
      }
    },
    async fetchProducts(catalogId) {
      if (!this.inboxId || !catalogId) return;

      this.loadingProducts = true;
      try {
        const response = await InboxesAPI.getWhatsAppProducts(
          this.inboxId,
          catalogId
        );
        this.products = response.data || [];
      } catch (error) {
        console.error('Failed to fetch products:', error);
        this.products = [];
      } finally {
        this.loadingProducts = false;
      }
    },
    async fetchFlows() {
      if (!this.inboxId) return;

      this.loadingFlows = true;
      try {
        const response = await InboxesAPI.getWhatsAppFlows(this.inboxId);
        this.flows = response.data || [];
      } catch (error) {
        console.error('Failed to fetch flows:', error);
        this.flows = [];
      } finally {
        this.loadingFlows = false;
      }
    },
    async fetchContacts(searchQuery = '') {
      this.loadingContacts = true;
      try {
        const response = await ContactsAPI.search(searchQuery, 1, 'name', '');
        this.availableContacts = response.data.payload || [];
      } catch (error) {
        console.error('Failed to fetch contacts:', error);
        this.availableContacts = [];
      } finally {
        this.loadingContacts = false;
      }
    },
    onContactSearchInput(query) {
      // Clear existing timeout
      if (this.contactSearchTimeout) {
        clearTimeout(this.contactSearchTimeout);
      }

      // Debounce search
      this.contactSearchTimeout = setTimeout(() => {
        this.fetchContacts(query);
      }, 300);
    },
    toggleContactSelection(contactId) {
      const index = this.selectedContactIds.indexOf(contactId);
      if (index > -1) {
        // Remove if already selected
        this.selectedContactIds.splice(index, 1);
      } else {
        // Add if not selected
        this.selectedContactIds.push(contactId);
      }
    },
    isContactSelected(contactId) {
      return this.selectedContactIds.includes(contactId);
    },
    removeSelectedContact(contactId) {
      const index = this.selectedContactIds.indexOf(contactId);
      if (index > -1) {
        this.selectedContactIds.splice(index, 1);
      }
    },
    getSelectedContacts() {
      return this.availableContacts.filter(contact =>
        this.selectedContactIds.includes(contact.id)
      );
    },
    addTelegramRow() {
      this.telegramButtonRows.push([{ text: '', type: 'callback', url: '' }]);
    },
    removeTelegramRow(rowIndex) {
      if (this.telegramButtonRows.length > 1) {
        this.telegramButtonRows.splice(rowIndex, 1);
      }
    },
    addTelegramButtonToRow(rowIndex) {
      this.telegramButtonRows[rowIndex].push({
        text: '',
        type: 'callback',
        url: '',
      });
    },
    removeTelegramButtonFromRow(rowIndex, btnIndex) {
      if (this.telegramButtonRows[rowIndex].length > 1) {
        this.telegramButtonRows[rowIndex].splice(btnIndex, 1);
      }
    },
    addFbQuickReply() {
      if (this.fbQuickReplies.length < 13) {
        this.fbQuickReplies.push({ text: '', type: 'text' });
      }
    },
    removeFbQuickReply(index) {
      if (this.fbQuickReplies.length > 1) {
        this.fbQuickReplies.splice(index, 1);
      }
    },
    addFbButton() {
      this.fbButtons.push({ text: '', type: 'url', value: '' });
    },
    removeFbButton(index) {
      if (this.fbButtons.length > 1) {
        this.fbButtons.splice(index, 1);
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <woot-modal-header
      :header-title="$t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.TITLE')"
      :header-content="$t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.SUBTITLE')"
    />

    <div class="row modal-content">
      <div class="medium-12 columns">
        <div class="interactive-builder-form">
          <!-- Message Content -->
          <div class="form-group relative">
            <label class="block text-sm font-medium">
              {{
                $t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.MESSAGE_CONTENT')
              }}
            </label>
            <WootMessageEditor
              v-model="messageContent"
              class="message-editor [&>div]:px-1 min-h-[80px]"
              enable-variables
              :enable-canned-responses="false"
              :enabled-menu-options="[]"
              :placeholder="
                $t(
                  'CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.MESSAGE_CONTENT_PLACEHOLDER'
                )
              "
            />
            <button
              type="button"
              class="absolute top-8 right-2 p-2 text-slate-500 hover:text-slate-700 focus:outline-none"
              @click="toggleEmojiPicker"
            >
              😀
            </button>
            <EmojiInput
              v-if="showEmojiPicker"
              v-on-clickaway="hideEmojiPicker"
              class="absolute top-20 right-0 z-50"
              :on-click="addEmojiToContent"
            />
            <div class="text-xs text-gray-500 text-right">
              {{ messageContent.length }}/1024
            </div>
          </div>

          <!-- Message Type Selection -->
          <div class="form-group">
            <label class="block text-sm font-medium">
              {{ $t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.MESSAGE_TYPE') }}
            </label>
            <div class="message-type-grid">
              <!-- Web Widget & API Supported Types (exclude WAHA) -->
              <label
                v-if="isWebWidgetOrAPI && !isWahaChannel"
                class="radio-item"
              >
                <input v-model="messageType" type="radio" value="options" />
                <span>Options</span>
              </label>
              <label
                v-if="isWebWidgetOrAPI && !isWahaChannel"
                class="radio-item"
              >
                <input v-model="messageType" type="radio" value="cards" />
                <span>Cards</span>
              </label>

              <!-- WhatsApp-specific Types -->
              <label v-if="isWhatsAppChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="buttons" />
                <span>Buttons</span>
              </label>
              <label v-if="isWhatsAppChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="list" />
                <span>List</span>
              </label>
              <label v-if="isWhatsAppChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="catalog_product"
                />
                <span>Product Catalog</span>
              </label>
              <label v-if="isWhatsAppChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="flow" />
                <span>WhatsApp Flow</span>
              </label>
              <label v-if="isWhatsAppChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="location" />
                <span>Location</span>
              </label>
              <label v-if="isWhatsAppChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="url_button" />
                <span>URL Button</span>
              </label>

              <!-- WAHA-specific Types -->
              <label v-if="isWahaChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="waha_poll" />
                <span>Poll</span>
              </label>
              <label v-if="isWahaChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="waha_contact"
                />
                <span>Contact</span>
              </label>
              <label v-if="isWahaChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="waha_list" />
                <span>List</span>
              </label>

              <!-- Telegram-specific Types -->
              <label v-if="isTelegramChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="telegram_buttons"
                />
                <span>Inline Keyboard</span>
              </label>
              <label v-if="isTelegramChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="telegram_poll"
                />
                <span>Poll</span>
              </label>

              <!-- Facebook-specific Types -->
              <label v-if="isFacebookChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="fb_quick_replies"
                />
                <span>Quick Replies</span>
              </label>
              <label v-if="isFacebookChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="cards" />
                <span>Generic Template</span>
              </label>
              <label v-if="isFacebookChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="fb_buttons" />
                <span>Buttons</span>
              </label>
              <label v-if="isFacebookChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="coupon" />
                <span>Coupon</span>
              </label>
              <!-- Temporarily disabled - Facebook API returns error
              <label v-if="isFacebookChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="customer_information"
                />
                <span>Customer Information</span>
              </label>
              -->

              <!-- Instagram-specific Types (reuses Facebook UI) -->
              <label v-if="isInstagramChannel" class="radio-item">
                <input
                  v-model="messageType"
                  type="radio"
                  value="fb_quick_replies"
                />
                <span>Quick Replies</span>
              </label>
              <label v-if="isInstagramChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="cards" />
                <span>Generic Template</span>
              </label>
              <label v-if="isInstagramChannel" class="radio-item">
                <input v-model="messageType" type="radio" value="fb_buttons" />
                <span>Buttons</span>
              </label>
            </div>
          </div>

          <!-- Options Section -->
          <div v-if="messageType === 'options'" class="form-group">
            <div class="options-header">
              <h3 class="options-title">Options</h3>
              <button type="button" class="button small" @click="addOption">
                Add Option
              </button>
            </div>

            <div class="space-y-2 max-h-64 overflow-y-auto">
              <div
                v-for="(option, index) in options"
                :key="index"
                class="flex items-center gap-2"
              >
                <input
                  v-model="options[index]"
                  :placeholder="`Option ${index + 1}`"
                  class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  class="button alert small"
                  :disabled="options.length <= 1"
                  @click="removeOption(index)"
                >
                  <fluent-icon icon="delete" />
                </button>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Cards Section -->
          <div v-if="messageType === 'cards'" class="form-group">
            <div class="options-header">
              <h3 class="options-title">Cards</h3>
              <button type="button" class="button small" @click="addCard">
                Add Card
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(card, cardIndex) in cards"
                :key="cardIndex"
                class="option-card"
              >
                <div class="flex justify-between items-start mb-3">
                  <h4 class="text-sm font-medium text-slate-700">
                    Card {{ cardIndex + 1 }}
                  </h4>
                  <button
                    type="button"
                    class="button alert small"
                    :disabled="cards.length <= 1"
                    @click="removeCard(cardIndex)"
                  >
                    <fluent-icon icon="delete" />
                  </button>
                </div>

                <div class="space-y-3">
                  <div>
                    <label class="block text-xs font-medium mb-1">Title *</label>
                    <input
                      v-model="card.title"
                      type="text"
                      placeholder="Card title"
                      class="form-control"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-medium mb-1">Description</label>
                    <textarea
                      v-model="card.description"
                      placeholder="Card description"
                      class="form-control"
                      rows="2"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-medium mb-1">Image URL</label>
                    <input
                      v-model="card.media_url"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      class="form-control"
                    />
                  </div>

                  <div>
                    <div class="flex justify-between items-center mb-2">
                      <label class="block text-xs font-medium">Action Buttons</label>
                      <button
                        type="button"
                        class="button tiny"
                        @click="addCardAction(cardIndex)"
                      >
                        Add Button
                      </button>
                    </div>

                    <div class="space-y-2">
                      <div
                        v-for="(action, actionIndex) in card.actions"
                        :key="actionIndex"
                        class="flex gap-2 items-start"
                      >
                        <div class="flex-1 space-y-2">
                          <input
                            v-model="action.text"
                            type="text"
                            placeholder="Button text"
                            class="form-control text-xs"
                          />
                          <select
                            v-model="action.type"
                            class="form-control text-xs"
                          >
                            <option value="link">Open URL</option>
                            <option value="postback">Send Reply</option>
                          </select>
                          <input
                            v-if="action.type === 'link'"
                            v-model="action.uri"
                            type="url"
                            placeholder="https://example.com"
                            class="form-control text-xs"
                          />
                          <input
                            v-else
                            v-model="action.payload"
                            type="text"
                            placeholder="Reply text (will autofill from button text)"
                            class="form-control text-xs"
                          />
                        </div>
                        <button
                          type="button"
                          class="button alert tiny mt-0"
                          :disabled="card.actions.length <= 1"
                          @click="removeCardAction(cardIndex, actionIndex)"
                        >
                          <fluent-icon icon="delete" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Header/Footer for buttons, lists, catalog_product, flow, and url_button -->
          <div
            v-if="
              [
                'buttons',
                'list',
                'catalog_product',
                'flow',
                'url_button',
              ].includes(messageType)
            "
            class="form-group"
          >
            <div class="flex items-center gap-2 mb-3">
              <input
                id="header-toggle"
                v-model="header.enabled"
                type="checkbox"
                class="form-checkbox"
              />
              <label for="header-toggle" class="font-medium cursor-pointer">
                Add Header
              </label>
            </div>

            <div v-if="header.enabled" class="pl-6 space-y-3">
              <!-- Header type selector only for buttons and url_button (lists only support text headers) -->
              <div v-if="['buttons', 'url_button'].includes(messageType)">
                <label class="block text-sm font-medium mb-2">Header Type</label>
                <div class="radio-group mb-3">
                  <label class="radio-item">
                    <input v-model="header.type" type="radio" value="text" />
                    <span>Text</span>
                  </label>
                  <label class="radio-item">
                    <input v-model="header.type" type="radio" value="image" />
                    <span>Image</span>
                  </label>
                  <label class="radio-item">
                    <input v-model="header.type" type="radio" value="video" />
                    <span>Video</span>
                  </label>
                  <label class="radio-item">
                    <input
                      v-model="header.type"
                      type="radio"
                      value="document"
                    />
                    <span>Document</span>
                  </label>
                </div>
              </div>

              <!-- For lists, always text. For buttons/url_button, depends on selection -->
              <div
                v-if="
                  messageType === 'list' ||
                  (['buttons', 'url_button'].includes(messageType) &&
                    header.type === 'text')
                "
              >
                <input
                  v-model="header.text"
                  type="text"
                  placeholder="Header text"
                  class="form-control"
                  maxlength="60"
                />
              </div>
              <!-- Media URL input only for buttons/url_button with media headers -->
              <div
                v-else-if="
                  ['buttons', 'url_button'].includes(messageType) &&
                  header.type !== 'text'
                "
              >
                <input
                  v-model="header.link"
                  type="url"
                  placeholder="Media URL (image, video, or document)"
                  class="form-control"
                />
              </div>
            </div>

            <div class="mt-3">
              <label class="block text-sm font-medium mb-2">
                Footer (optional)
              </label>
              <input
                v-model="footer"
                type="text"
                placeholder="Footer text"
                class="form-control"
                maxlength="60"
              />
            </div>
          </div>

          <!-- Product Catalog Configuration -->
          <div v-if="messageType === 'catalog_product'" class="form-group">
            <h3 class="options-title mb-3">Product Catalog Configuration</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Select Catalog *
                </label>
                <select
                  v-model="catalogId"
                  class="form-control"
                  :disabled="loadingCatalogs"
                >
                  <option value="" disabled>
                    {{
                      loadingCatalogs
                        ? 'Loading catalogs...'
                        : catalogs.length === 0
                          ? 'No catalogs available'
                          : 'Select a catalog'
                    }}
                  </option>
                  <option
                    v-for="catalog in catalogs"
                    :key="catalog.id"
                    :value="catalog.id"
                  >
                    {{ catalog.name }}
                  </option>
                </select>
              </div>

              <div>
                <div class="flex justify-between items-center mb-2">
                  <label class="block text-sm font-medium">
                    Select Products *
                  </label>
                  <button
                    type="button"
                    class="button small"
                    @click="addProductToCatalog"
                  >
                    Add Product
                  </button>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="(productId, index) in selectedProducts"
                    :key="index"
                    class="flex items-center gap-2"
                  >
                    <select
                      v-model="selectedProducts[index]"
                      class="form-control flex-1"
                      :disabled="loadingProducts || !catalogId"
                    >
                      <option value="" disabled>
                        {{
                          !catalogId
                            ? 'Select a catalog first'
                            : loadingProducts
                              ? 'Loading products...'
                              : products.length === 0
                                ? 'No products available'
                                : 'Select a product'
                        }}
                      </option>
                      <option
                        v-for="product in products"
                        :key="product.id"
                        :value="product.retailer_id"
                      >
                        {{ product.name }}
                      </option>
                    </select>
                    <button
                      type="button"
                      class="button alert small"
                      :disabled="selectedProducts.length <= 1"
                      @click="removeProductFromCatalog(index)"
                    >
                      <fluent-icon icon="delete" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Flow Configuration -->
          <div v-if="messageType === 'flow'" class="form-group">
            <h3 class="options-title mb-3">WhatsApp Flow Configuration</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Select Flow *
                </label>
                <select
                  v-model="flowId"
                  class="form-control"
                  :disabled="loadingFlows"
                >
                  <option value="" disabled>
                    {{
                      loadingFlows
                        ? 'Loading flows...'
                        : flows.length === 0
                          ? 'No flows available'
                          : 'Select a flow'
                    }}
                  </option>
                  <option v-for="flow in flows" :key="flow.id" :value="flow.id">
                    {{ flow.name }} ({{ flow.status }})
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">
                  Button Text (CTA) *
                </label>
                <input
                  v-model="flowCta"
                  type="text"
                  placeholder="e.g., 'Start Survey', 'Open Form'"
                  class="form-control"
                  maxlength="20"
                />
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- URL Button Configuration -->
          <div v-if="messageType === 'url_button'" class="form-group">
            <h3 class="options-title mb-3">URL Button Configuration</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Button Title *
                </label>
                <input
                  v-model="urlButtonTitle"
                  type="text"
                  placeholder="e.g., 'Visit Website'"
                  class="form-control"
                  maxlength="24"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">
                  Button URL *
                </label>
                <input
                  v-model="urlButtonUrl"
                  type="url"
                  placeholder="https://example.com"
                  class="form-control"
                />
              </div>
            </div>

            <!-- Validation Messages for URL Button -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Location Configuration -->
          <div v-if="messageType === 'location'" class="form-group">
            <h3 class="options-title mb-3">Location Configuration</h3>
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium mb-2">
                    Latitude *
                  </label>
                  <input
                    v-model="latitude"
                    type="number"
                    step="any"
                    placeholder="-90 to 90"
                    class="form-control"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium mb-2">
                    Longitude *
                  </label>
                  <input
                    v-model="longitude"
                    type="number"
                    step="any"
                    placeholder="-180 to 180"
                    class="form-control"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">
                  Location Name (optional)
                </label>
                <input
                  v-model="locationName"
                  type="text"
                  placeholder="e.g., 'Golden Gate Bridge'"
                  class="form-control"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">
                  Address (optional)
                </label>
                <input
                  v-model="locationAddress"
                  type="text"
                  placeholder="e.g., 'Golden Gate Bridge, San Francisco, CA'"
                  class="form-control"
                />
              </div>
            </div>
          </div>

          <!-- Buttons Section (WhatsApp Quick Reply Buttons) -->
          <div v-if="messageType === 'buttons'" class="form-group">
            <h3 class="options-title mb-3">Quick Reply Buttons (Max 3)</h3>
            <div class="space-y-3">
              <div
                v-for="(button, index) in buttons"
                :key="index"
                class="flex items-center gap-2"
              >
                <span class="text-sm font-medium text-slate-600 w-8">
                  {{ index + 1 }}
                </span>
                <input
                  v-model="buttons[index]"
                  type="text"
                  :placeholder="`Button ${index + 1} text (max 20 chars)`"
                  class="form-control flex-1"
                  maxlength="20"
                />
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-2">
              WhatsApp allows up to 3 quick reply buttons. Leave empty to hide
              unused buttons.
            </p>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- List Section (WhatsApp List Menu) -->
          <div v-if="messageType === 'list'" class="form-group">
            <!-- Button Text -->
            <div class="mb-4">
              <label class="block text-sm font-medium mb-2">
                List Button Text *
              </label>
              <input
                v-model="buttonText"
                type="text"
                placeholder="e.g., 'View Options', 'Select Item'"
                class="form-control"
                maxlength="20"
              />
              <p class="text-xs text-gray-500 mt-1">
                Text shown on the button that opens the list menu
              </p>
            </div>

            <!-- List Sections -->
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <h3 class="options-title">List Sections</h3>
                <button
                  type="button"
                  class="button small"
                  @click="addListSection"
                >
                  Add Section
                </button>
              </div>

              <div
                v-for="(section, sectionIndex) in listSections"
                :key="sectionIndex"
                class="option-card"
              >
                <div class="flex justify-between items-start mb-3">
                  <input
                    v-model="section.title"
                    type="text"
                    placeholder="Section Title"
                    class="form-control flex-1 mr-2"
                    maxlength="24"
                  />
                  <button
                    type="button"
                    class="button alert small"
                    :disabled="listSections.length <= 1"
                    @click="removeListSection(sectionIndex)"
                  >
                    <fluent-icon icon="delete" />
                  </button>
                </div>

                <!-- Section Rows -->
                <div class="pl-4 space-y-2">
                  <div class="flex justify-between items-center mb-2">
                    <label class="text-xs font-medium">Rows</label>
                    <button
                      type="button"
                      class="button tiny"
                      @click="addListRow(sectionIndex)"
                    >
                      Add Row
                    </button>
                  </div>

                  <div
                    v-for="(row, rowIndex) in section.rows"
                    :key="rowIndex"
                    class="flex gap-2 items-start"
                  >
                    <div class="flex-1 space-y-2">
                      <input
                        v-model="row.title"
                        type="text"
                        placeholder="Row title *"
                        class="form-control text-xs"
                        maxlength="24"
                      />
                      <input
                        v-model="row.description"
                        type="text"
                        placeholder="Description (optional)"
                        class="form-control text-xs"
                        maxlength="72"
                      />
                    </div>
                    <button
                      type="button"
                      class="button alert tiny mt-0"
                      :disabled="section.rows.length <= 1"
                      @click="removeListRow(sectionIndex, rowIndex)"
                    >
                      <fluent-icon icon="delete" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- WAHA Poll Section -->
          <div v-if="messageType === 'waha_poll'" class="form-group">
            <h3 class="options-title mb-3">Poll Configuration</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Poll Question *
                </label>
                <input
                  v-model="pollName"
                  type="text"
                  placeholder="e.g., 'How satisfied are you with our service?'"
                  class="form-control"
                />
              </div>

              <div>
                <div class="flex justify-between items-center mb-2">
                  <label class="block text-sm font-medium">
                    Poll Options *
                  </label>
                  <button
                    type="button"
                    class="button small"
                    @click="pollOptions.push('')"
                  >
                    Add Option
                  </button>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="(option, index) in pollOptions"
                    :key="index"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model="pollOptions[index]"
                      type="text"
                      :placeholder="`Option ${index + 1}`"
                      class="form-control flex-1"
                    />
                    <button
                      type="button"
                      class="button alert small"
                      :disabled="pollOptions.length <= 2"
                      @click="pollOptions.splice(index, 1)"
                    >
                      <fluent-icon icon="delete" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <input
                  id="poll-multi-select"
                  v-model="pollMultiSelect"
                  type="checkbox"
                  class="form-checkbox"
                />
                <label for="poll-multi-select" class="cursor-pointer">
                  Allow multiple selections
                </label>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- WAHA Contact Section -->
          <div v-if="messageType === 'waha_contact'" class="form-group">
            <h3 class="options-title mb-3">Select Contacts to Share</h3>
            <p class="text-sm text-gray-600 mb-3">
              Select one or more contacts from your VowChat contacts to share as
              vCards
            </p>

            <div class="space-y-3">
              <!-- Selected Contacts Pills -->
              <div
                v-if="selectedContactIds.length > 0"
                class="flex flex-wrap gap-2 mb-3"
              >
                <div
                  v-for="contactId in selectedContactIds"
                  :key="contactId"
                  class="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  <span>{{
                    availableContacts.find(c => c.id === contactId)?.name ||
                    'Unknown'
                  }}</span>
                  <button
                    type="button"
                    class="ml-1 text-blue-600 hover:text-blue-800"
                    @click="removeSelectedContact(contactId)"
                  >
                    ×
                  </button>
                </div>
              </div>

              <!-- Search Input -->
              <div>
                <label class="block text-sm font-medium mb-2">
                  Search Contacts *
                </label>
                <input
                  v-model="contactSearchQuery"
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  class="form-control"
                  @input="onContactSearchInput(contactSearchQuery)"
                />
              </div>

              <!-- Contact List -->
              <div
                v-if="availableContacts.length > 0"
                class="border border-gray-200 rounded max-h-64 overflow-y-auto"
              >
                <div
                  v-for="contact in availableContacts"
                  :key="contact.id"
                  class="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  @click="toggleContactSelection(contact.id)"
                >
                  <input
                    type="checkbox"
                    :checked="isContactSelected(contact.id)"
                    class="form-checkbox"
                    @click.stop="toggleContactSelection(contact.id)"
                  />
                  <div class="flex-1">
                    <div class="font-medium text-sm">
                      {{ contact.name || 'Unknown' }}
                    </div>
                    <div class="text-xs text-gray-500">
                      <span v-if="contact.email">{{ contact.email }}</span>
                      <span v-if="contact.email && contact.phone_number">
                        •
                      </span>
                      <span v-if="contact.phone_number">{{
                        contact.phone_number
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading State -->
              <div
                v-else-if="loadingContacts"
                class="text-center py-4 text-gray-500"
              >
                Loading contacts...
              </div>

              <!-- Empty State -->
              <div
                v-else-if="contactSearchQuery"
                class="text-center py-4 text-gray-500"
              >
                No contacts found. Try a different search.
              </div>
              <div v-else class="text-center py-4 text-gray-500">
                Start typing to search for contacts
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- WAHA List Section -->
          <div v-if="messageType === 'waha_list'" class="form-group">
            <!-- Button Text -->
            <div class="mb-4">
              <label class="block text-sm font-medium mb-2">
                List Button Text *
              </label>
              <input
                v-model="buttonText"
                type="text"
                placeholder="e.g., 'View Options', 'Select Item'"
                class="form-control"
                maxlength="20"
              />
              <p class="text-xs text-gray-500 mt-1">
                Text shown on the button that opens the list menu
              </p>
            </div>

            <!-- List Sections -->
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <h3 class="options-title">List Sections</h3>
                <button
                  type="button"
                  class="button small"
                  @click="addListSection"
                >
                  Add Section
                </button>
              </div>

              <div
                v-for="(section, sectionIndex) in listSections"
                :key="sectionIndex"
                class="option-card"
              >
                <div class="flex justify-between items-start mb-3">
                  <input
                    v-model="section.title"
                    type="text"
                    placeholder="Section Title"
                    class="form-control flex-1 mr-2"
                    maxlength="24"
                  />
                  <button
                    type="button"
                    class="button alert small"
                    :disabled="listSections.length <= 1"
                    @click="removeListSection(sectionIndex)"
                  >
                    <fluent-icon icon="delete" />
                  </button>
                </div>

                <!-- Section Rows -->
                <div class="pl-4 space-y-2">
                  <div class="flex justify-between items-center mb-2">
                    <label class="text-xs font-medium">Rows</label>
                    <button
                      type="button"
                      class="button tiny"
                      @click="addListRow(sectionIndex)"
                    >
                      Add Row
                    </button>
                  </div>

                  <div
                    v-for="(row, rowIndex) in section.rows"
                    :key="rowIndex"
                    class="flex gap-2 items-start"
                  >
                    <div class="flex-1 space-y-2">
                      <input
                        v-model="row.title"
                        type="text"
                        placeholder="Row title *"
                        class="form-control text-xs"
                        maxlength="24"
                      />
                      <input
                        v-model="row.description"
                        type="text"
                        placeholder="Description (optional)"
                        class="form-control text-xs"
                        maxlength="72"
                      />
                    </div>
                    <button
                      type="button"
                      class="button alert tiny mt-0"
                      :disabled="section.rows.length <= 1"
                      @click="removeListRow(sectionIndex, rowIndex)"
                    >
                      <fluent-icon icon="delete" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Telegram Poll Section -->
          <div v-if="messageType === 'telegram_poll'" class="form-group">
            <h3 class="options-title mb-3">Poll Configuration</h3>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Poll Question *
                </label>
                <input
                  v-model="telegramPollQuestion"
                  type="text"
                  placeholder="e.g., 'What is your favorite feature?'"
                  class="form-control"
                  maxlength="300"
                />
              </div>

              <div>
                <div class="flex justify-between items-center mb-2">
                  <label class="block text-sm font-medium">
                    Poll Options * (2-10 options)
                  </label>
                  <button
                    type="button"
                    class="button small"
                    :disabled="telegramPollOptions.length >= 10"
                    @click="telegramPollOptions.push('')"
                  >
                    Add Option
                  </button>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="(option, index) in telegramPollOptions"
                    :key="index"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model="telegramPollOptions[index]"
                      type="text"
                      :placeholder="`Option ${index + 1} (max 100 chars)`"
                      class="form-control flex-1"
                      maxlength="100"
                    />
                    <button
                      type="button"
                      class="button alert small"
                      :disabled="telegramPollOptions.length <= 2"
                      @click="telegramPollOptions.splice(index, 1)"
                    >
                      <fluent-icon icon="delete" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <input
                  id="poll-multiple"
                  v-model="telegramPollAllowsMultiple"
                  type="checkbox"
                  class="form-checkbox"
                />
                <label for="poll-multiple" class="cursor-pointer">
                  Allow multiple answers
                </label>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Facebook Quick Replies Section -->
          <div v-if="messageType === 'fb_quick_replies'" class="form-group">
            <div class="flex justify-between items-center mb-3">
              <h3 class="options-title">Quick Replies (Max 13)</h3>
              <button
                type="button"
                class="button small"
                :disabled="fbQuickReplies.length >= 13"
                @click="addFbQuickReply"
              >
                Add Quick Reply
              </button>
            </div>

            <div class="space-y-3">
              <div
                v-for="(reply, index) in fbQuickReplies"
                :key="index"
                class="border border-slate-200 rounded p-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-xs font-medium text-slate-600 w-8">{{
                    index + 1
                  }}</span>
                  <input
                    v-model="reply.text"
                    type="text"
                    :placeholder="`Quick reply ${index + 1} (max 20 chars)`"
                    class="form-control flex-1 text-sm"
                    maxlength="20"
                  />
                  <button
                    type="button"
                    class="button alert small"
                    :disabled="fbQuickReplies.length <= 1"
                    @click="removeFbQuickReply(index)"
                  >
                    <fluent-icon icon="delete" />
                  </button>
                </div>
              </div>
            </div>

            <p class="text-xs text-gray-500 mt-2">
              Facebook supports up to 13 quick replies. Clicking a quick reply
              sends the text back as a message.
            </p>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Facebook Buttons Section (URL, Coupon, Postback) -->
          <div v-if="messageType === 'fb_buttons'" class="form-group">
            <div class="flex justify-between items-center mb-3">
              <h3 class="options-title">Buttons (Max 3)</h3>
              <button
                type="button"
                class="button small"
                :disabled="fbButtons.length >= 3"
                @click="addFbButton"
              >
                Add Button
              </button>
            </div>

            <div class="space-y-3">
              <div
                v-for="(button, index) in fbButtons"
                :key="index"
                class="border border-slate-200 rounded p-3"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="text-sm font-medium text-slate-700">
                    Button {{ index + 1 }}
                  </span>
                  <button
                    type="button"
                    class="button alert small"
                    :disabled="fbButtons.length <= 1"
                    @click="removeFbButton(index)"
                  >
                    <fluent-icon icon="delete" />
                  </button>
                </div>

                <div class="space-y-2">
                  <div>
                    <label class="block text-xs font-medium mb-1">
                      Button Text *
                    </label>
                    <input
                      v-model="button.text"
                      type="text"
                      placeholder="Button text (max 20 chars)"
                      class="form-control text-sm"
                      maxlength="20"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-medium mb-1">
                      Button Type *
                    </label>
                    <select v-model="button.type" class="form-control text-sm">
                      <option value="url">URL (Open Link)</option>
                      <option value="postback">Postback (Send Reply)</option>
                    </select>
                  </div>

                  <div v-if="button.type === 'url'">
                    <label class="block text-xs font-medium mb-1">URL *</label>
                    <input
                      v-model="button.value"
                      type="url"
                      placeholder="https://example.com"
                      class="form-control text-sm"
                    />
                  </div>

                  <div v-else>
                    <label class="block text-xs font-medium mb-1">
                      Payload (optional)
                    </label>
                    <input
                      v-model="button.value"
                      type="text"
                      placeholder="Custom payload (will autofill from button text)"
                      class="form-control text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <p class="text-xs text-gray-500 mt-2">
              Facebook supports up to 3 buttons. Choose URL for links or
              Postback for callbacks.
            </p>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Facebook Coupon Template Section -->
          <div v-if="messageType === 'coupon'" class="form-group">
            <h3 class="options-title mb-3">Coupon Template</h3>
            <p class="text-sm text-gray-600 mb-3">
              Share a coupon code with customers. Optionally include a URL for
              more details.
            </p>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Coupon Code *
                </label>
                <input
                  v-model="couponCode"
                  type="text"
                  placeholder="e.g., SAVE20, WINTER2024"
                  class="form-control"
                />
                <p class="text-xs text-gray-500 mt-1">
                  The coupon or promo code to share with the customer
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">
                  Coupon URL (optional)
                </label>
                <input
                  v-model="couponUrl"
                  type="url"
                  placeholder="https://example.com/offers"
                  class="form-control"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Optional link to more details about the offer
                </p>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Facebook Customer Information Template Section -->
          <div v-if="messageType === 'customer_information'" class="form-group">
            <h3 class="options-title mb-3">Customer Information Template</h3>
            <p class="text-sm text-gray-600 mb-3">
              Collect shipping address and contact information from customers.
              Facebook will show a standard form.
            </p>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-2">
                  Business Privacy URL *
                </label>
                <input
                  v-model="customerInfoPrivacyUrl"
                  type="url"
                  placeholder="https://example.com/privacy-policy"
                  class="form-control"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Link to your business privacy policy (required by Facebook)
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium mb-2">
                  Countries (optional)
                </label>
                <input
                  v-model="customerInfoCountries"
                  type="text"
                  placeholder="US, CA, GB (comma separated)"
                  class="form-control"
                  @input="
                    customerInfoCountries = $event.target.value
                      .split(',')
                      .map(c => c.trim())
                      .filter(c => c)
                  "
                />
                <p class="text-xs text-gray-500 mt-1">
                  Limit to specific countries (ISO 2-letter codes). Leave empty
                  for all countries.
                </p>
              </div>
            </div>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Telegram Inline Keyboard Section -->
          <div v-if="messageType === 'telegram_buttons'" class="form-group">
            <div class="flex justify-between items-center mb-3">
              <h3 class="options-title">Inline Keyboard Grid</h3>
              <button
                type="button"
                class="button small"
                @click="addTelegramRow"
              >
                Add Row
              </button>
            </div>

            <div class="space-y-3">
              <div
                v-for="(row, rowIndex) in telegramButtonRows"
                :key="rowIndex"
                class="option-card"
              >
                <div class="flex justify-between items-center mb-2">
                  <h4 class="text-sm font-medium text-slate-700">
                    Row {{ rowIndex + 1 }}
                  </h4>
                  <button
                    type="button"
                    class="button alert small"
                    :disabled="telegramButtonRows.length <= 1"
                    @click="removeTelegramRow(rowIndex)"
                  >
                    <fluent-icon icon="delete" />
                  </button>
                </div>

                <div class="flex justify-between items-center mb-2">
                  <label class="text-xs font-medium">Buttons</label>
                  <button
                    type="button"
                    class="button tiny"
                    @click="addTelegramButtonToRow(rowIndex)"
                  >
                    Add Button
                  </button>
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(button, btnIndex) in row"
                    :key="btnIndex"
                    class="border border-slate-200 rounded p-3"
                  >
                    <div class="flex items-center gap-2 mb-2">
                      <span class="text-xs font-medium text-slate-600 w-8">{{
                        btnIndex + 1
                      }}</span>
                      <input
                        v-model="button.text"
                        type="text"
                        :placeholder="`Button ${btnIndex + 1} text (max 64 chars)`"
                        class="form-control flex-1 text-sm"
                        maxlength="64"
                      />
                      <button
                        type="button"
                        class="button alert small"
                        :disabled="row.length <= 1"
                        @click="removeTelegramButtonFromRow(rowIndex, btnIndex)"
                      >
                        <fluent-icon icon="delete" />
                      </button>
                    </div>

                    <div class="pl-10 space-y-2">
                      <div class="flex items-center gap-4">
                        <label class="flex items-center gap-2 cursor-pointer">
                          <input
                            v-model="button.type"
                            type="radio"
                            value="callback"
                            class="form-radio"
                          />
                          <span class="text-xs">Callback (action)</span>
                        </label>
                        <label class="flex items-center gap-2 cursor-pointer">
                          <input
                            v-model="button.type"
                            type="radio"
                            value="url"
                            class="form-radio"
                          />
                          <span class="text-xs">URL (link)</span>
                        </label>
                      </div>

                      <div v-if="button.type === 'url'">
                        <input
                          v-model="button.url"
                          type="url"
                          placeholder="https://example.com"
                          class="form-control text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p class="text-xs text-gray-500 mt-2">
              Telegram supports unlimited buttons arranged in a grid. Each row
              can have multiple buttons side by side. Callback buttons trigger
              actions, while URL buttons open links.
            </p>

            <!-- Validation Messages -->
            <div v-if="validationError" class="callout alert mt-3">
              {{ validationError }}
            </div>
          </div>

          <!-- Actions -->
          <div class="form-group form-actions">
            <button type="button" class="button clear" @click="onClose">
              {{ $t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.CANCEL') }}
            </button>
            <button
              type="button"
              class="button success"
              :disabled="!isValid"
              @click="sendInteractiveMessage"
            >
              {{ $t('CONVERSATION.INTERACTIVE_MESSAGE_BUILDER.SEND') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[5rem];

    p {
      @apply text-base;
    }
  }
}

.interactive-builder-form {
  padding: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--s-600);
}

.radio-group {
  display: flex;
  gap: 1rem;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal;
  margin-bottom: 0;
}

.message-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.options-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.options-title {
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  color: var(--s-600);
}

.option-card {
  border: 1px solid var(--s-100);
  border-radius: 8px;
  padding: 1rem;
  background: var(--white);
}

.option-card-content {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.option-inputs {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option-inputs input {
  width: 100%;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
}
</style>
